import { z } from 'zod';

export const invoiceLineSchema = z.object({
  lineId: z.string(),
  poLineId: z.string().optional(),
  itemId: z.string(),
  itemName: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitPrice: z.number(),
  totalPrice: z.number(),
});

export const invoiceSchema = z.object({
  invoiceId: z.string(),
  invoiceNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  buyerName: z.string(),
  poId: z.string().optional(),
  poNumber: z.string().optional(),
  status: z.enum(['draft', 'submitted', 'pending', 'matched', 'disputed', 'approved', 'paid']),
  invoiceDate: z.string(),
  dueDate: z.string(),
  lines: z.array(invoiceLineSchema),
  subtotal: z.number(),
  tax: z.number(),
  total: z.number(),
  currency: z.string(),
  paymentStatus: z.enum(['unpaid', 'partial', 'paid']).optional(),
  paymentDate: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type InvoiceLine = z.infer<typeof invoiceLineSchema>;
export type Invoice = z.infer<typeof invoiceSchema>;
export type InvoiceStatus = Invoice['status'];
export type PaymentStatus = NonNullable<Invoice['paymentStatus']>;

export const invoicesResponseSchema = z.object({
  data: z.array(invoiceSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number() 
  }),
  meta: z.object({
    totalAmount: z.number(),
    paidAmount: z.number(),
    pendingAmount: z.number() 
  }),
});

export type InvoicesResponse = z.infer<typeof invoicesResponseSchema>;

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending: 'Under Review',
  matched: 'Matched',
  disputed: 'Disputed',
  approved: 'Approved',
  paid: 'Paid',
};

export const INVOICE_STATUS_COLORS: Record<InvoiceStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  matched: 'bg-cyan-100 text-cyan-800',
  disputed: 'bg-red-100 text-red-800',
  approved: 'bg-green-100 text-green-800',
  paid: 'bg-emerald-100 text-emerald-800',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: 'Unpaid',
  partial: 'Partial Payment',
  paid: 'Paid',
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  unpaid: 'bg-gray-100 text-gray-800',
  partial: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
};
